/**
 * This file was auto-generated by client-types. Do not manually edit this file.
 * Your changes would be automatically overwritten.
 *
 * Service Documentation: https://private.hubteam.com/shoebill/service/com.hubspot.purchase.motions.rpc.PurchaseMotionsEvaluationService
 */

import type {
  ChirpAvroRpcDetails,
  Requestify,
  unhandledEnumCase,
  Default,
  HubspotMetadata,
} from 'rpc-client-utils';

/**
 * Record for com.hubspot.chirp.core.CausalChainLink
 */
export type CausalChainLink = {
  coordinates: ChirpMethodCoordinates;
  errorClass: string;
  errorMessage?: string;
};

/**
 * Record for com.hubspot.chirp.core.ChirpMethodCoordinates
 */
export type ChirpMethodCoordinates = { service: string; method: string };

export type EmptyError = {
  chirpErrorMessage?: string;
  coordinates?: ChirpMethodCoordinates;
  causalChain: Default<CausalChainLink[]>;
  shouldRetry?: boolean;
};

export type QuantityName =
  | 'CORE_SEAT'
  | 'SALES_SEAT'
  | 'SERVICE_SEAT'
  | 'COMMERCE_SEAT'
  | 'REVENUE_SEAT'
  | 'FLEX_SEAT'
  | 'FRONT_OFFICE_SEAT'
  | 'OPS_SEAT'
  | 'GTM_SEAT'
  | 'CONTACTS'
  | 'EMAIL_SENDS'
  | 'TRANSCRIPT_HOURS'
  | 'SMS_INBOUND_OUTBOUND'
  | 'CUSTOM_PROPERTIES'
  | 'WHATSAPP_CONVERSATIONS'
  | 'EMBEDDABLE_VIDEOS'
  | 'CALCULATED_PROPERTIES'
  | 'CALLING_MINUTES_PER_PORTAL'
  | 'CALLING_PURCHASED_PHONE_NUMBERS_PER_PORTAL'
  | 'USERS'
  | 'BRAND_DOMAINS'
  | 'WORKFLOWS'
  | 'TEAMS'
  | 'OBJECT_LISTS_DYNAMIC'
  | 'CRM_CONTACTS'
  | 'BREEZE_INTELLIGENCE_CREDITS'
  | 'CRM_OBJECTS'
  | 'API_CALLS_DAILY'
  | 'HUBSPOT_CREDITS'
  | 'BUCKETED_OBJECTS'
  | 'CUSTOM_EVENTS'
  | 'NON_AI_WORKFLOW_ACTIONS'
  | 'AEO_ANSWERS'
  | 'CONVERSATIONAL_RECORDS'
  | 'PROMPTS'
  | 'CUSTOM_OBJECTS'
  | 'DASHBOARDS'
  | 'CUSTOM_REPORTS'
  | 'RUNS'
  | 'QUERIES'
  | 'UNIT'
  | unhandledEnumCase;

export type EvaluateAssignabilityRequest = {};

export type EvaluateAssignabilityResponse = {
  assignable: boolean;
  repInfo?: NurturingOwnerInfo;
  selfServiceEligible: Default<boolean>;
};

/**
 *  CHIRP-compatible version of nurturing owner information.
 *  <p>This is a CHIRP-compatible equivalent of {@code com.hubspot.nurturingowners.core.NurturingOwnerInfo}
 *  that can be used in CHIRP RPC messages.
 */
export type NurturingOwnerInfo = {
  link?: string;
  /** @deprecated Use getFirstName() and getLastName() instead */
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatarLink?: string;
  phoneNumber?: string;
};

export type SelfServiceEligibilityRequest = {};

export type SelfServiceEligibilityResponse = { selfServiceEligible: boolean };

export type PurchaseMotion =
  | 'BUY_TOUCHLESS'
  | 'TALK_TO_SALES'
  | 'TRIAL'
  | 'TALK_TO_SALES_FALLBACK'
  | 'BUY_ADDITIONAL_CONTACTS'
  | 'BUY_ADDITIONAL_SEATS'
  | 'REQUEST_SEAT'
  | 'ASSIGN_SEAT'
  | 'PRICING_PAGE_REDIRECT'
  | 'VIEW_ACCOUNT'
  | 'REQUEST_ACCESS'
  | 'BLOCKED'
  | 'REQUEST_PRODUCT'
  | unhandledEnumCase;

/**
 *  Per-cart evaluation result.
 *  <p>{@code cartPurchaseMotions} is the cart-level decision (per-product intersection base + HSoC
 *  overrides). {@code purchaseMotionsByProductApiName} carries per-product motions that sit
 *  alongside the cart motion (e.g. {@code BUY_ADDITIONAL_SEATS}).
 */
export type CartEvaluationResult = {
  cartKey: string;
  cartPurchaseMotions: Default<PurchaseMotion[]>;
  purchaseMotionsByProductApiName: Default<Record<string, PurchaseMotion[]>>;
};

/**
 *  One product line in a cart. {@code quantityName} is present only for quantity-pack SKUs — products
 *  that share a product api name but resolve to a different SKU by quantity name (e.g. marketing
 *  contacts); it is absent for the base product.
 */
export type CartLineItem = {
  productApiName: string;
  quantityName?: QuantityName;
  incrementalSkuQuantity: number;
};

/**
 *  A single hypothetical purchase to evaluate.
 *  <p>{@code resolvedCartCostUsd} is an optional, transitional pass-in: when present the service
 *  uses it for the ASP band; when absent the service self-computes the cost (lazily, only when the
 *  band is reached). It will be removed once the service self-computes at parity.
 */
export type CartRequest = {
  cartKey: Default<string>;
  lineItems: Default<CartLineItem[]>;
  resolvedCartCostUsd?: string;
  preferredTerm?: CartTerm;
};

/**
 *  The commitment/payment term pair a cart is being purchased at — the PurchaseOptions {@code Term}
 *  identity. Supplied as an optional pricing hint so a self-computed ASP band cost uses the term the
 *  buyer selected rather than a default one.
 */
export type CartTerm = { commitmentTerm: number; paymentTerm: number };

export type DtbEvaluationRequest = { productApiNames: Default<string[]> };

export type EvaluationResponse = { purchaseMotionsMapping: ProductsByLocation };

/**
 *  Authenticated cart-based purchase-motion evaluation request. One or more resolved carts are
 *  evaluated for the portal/user in the ChirpContext against a single location/surface; portal-level
 *  context is resolved once and shared across every cart. Tier-options surfaces send one cart per
 *  tier card. One location per request keeps each cart's result unambiguous (correlatable by
 *  cartKey); callers needing another surface issue a separate request.
 */
export type InAppCartsEvaluationRequest = {
  carts: Default<CartRequest[]>;
  location: string;
};

/**
 * One {@link CartEvaluationResult} per input cart, order-preserved and correlatable via cartKey.
 */
export type InAppCartsEvaluationResponse = {
  results: Default<CartEvaluationResult[]>;
};

export type InAppEvaluationRequest = {
  productApiNames: Default<string[]>;
  locations: Default<string[]>;
  purchaseMotionFilterParameters?: PurchaseMotionFilterParameters;
  productQuantities: Default<Record<string, number>>;
};

export type ProductsByLocation = {
  productsByLocationString: Default<Record<string, PurchaseMotionsByProduct>>;
};

export type PurchaseMotionFilterParameters = { seatCount?: number };

export type PurchaseMotionsByPriority = {
  purchaseMotions: Default<PurchaseMotion[]>;
};

export type PurchaseMotionsByProduct = {
  purchaseMotionsByProductApiName: Default<
    Record<string, PurchaseMotionsByPriority>
  >;
};

export type EvaluateInAppRequestType = Requestify<InAppEvaluationRequest>;
export type EvaluateInAppResponseType = EvaluationResponse;
export type EvaluateInAppErrorType = EmptyError & HubspotMetadata;
export const evaluateInAppRpc: ChirpAvroRpcDetails<
  EvaluateInAppRequestType,
  EvaluateInAppResponseType,
  EvaluateInAppErrorType,
  false
> = {
  serviceName:
    'com.hubspot.purchase.motions.rpc.PurchaseMotionsEvaluationService',
  rpcName: 'evaluateInApp',
  auth: 'app',
  fingerprint: 'KDhTXICuQlXrifxQofJJG5wP6LXzGzX8',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type EvaluateInAppV2RequestType = Requestify<InAppEvaluationRequest>;
export type EvaluateInAppV2ResponseType = EvaluationResponse;
export type EvaluateInAppV2ErrorType = EmptyError & HubspotMetadata;
export const evaluateInAppV2Rpc: ChirpAvroRpcDetails<
  EvaluateInAppV2RequestType,
  EvaluateInAppV2ResponseType,
  EvaluateInAppV2ErrorType,
  false
> = {
  serviceName:
    'com.hubspot.purchase.motions.rpc.PurchaseMotionsEvaluationService',
  rpcName: 'evaluateInAppV2',
  auth: 'app',
  fingerprint: 'KDhTXICuQlXrifxQofJJG5wP6LXzGzX8',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type EvaluateInAppForDtbRequestType = Requestify<DtbEvaluationRequest>;
export type EvaluateInAppForDtbResponseType = EvaluationResponse;
export type EvaluateInAppForDtbErrorType = EmptyError & HubspotMetadata;
export const evaluateInAppForDtbRpc: ChirpAvroRpcDetails<
  EvaluateInAppForDtbRequestType,
  EvaluateInAppForDtbResponseType,
  EvaluateInAppForDtbErrorType,
  false
> = {
  serviceName:
    'com.hubspot.purchase.motions.rpc.PurchaseMotionsEvaluationService',
  rpcName: 'evaluateInAppForDtb',
  auth: 'app',
  fingerprint: 'zMSUx6SvZEbrifxQofJJG5wP6LXzGzX8',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type EvaluateInAppCartsRequestType =
  Requestify<InAppCartsEvaluationRequest>;
export type EvaluateInAppCartsResponseType = InAppCartsEvaluationResponse;
export type EvaluateInAppCartsErrorType = EmptyError & HubspotMetadata;
export const evaluateInAppCartsRpc: ChirpAvroRpcDetails<
  EvaluateInAppCartsRequestType,
  EvaluateInAppCartsResponseType,
  EvaluateInAppCartsErrorType,
  false
> = {
  serviceName:
    'com.hubspot.purchase.motions.rpc.PurchaseMotionsEvaluationService',
  rpcName: 'evaluateInAppCarts',
  auth: 'app',
  fingerprint: 'F5lKwA8pjUkGg56HI9en_pwP6LXzGzX8',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetSelfServiceEligibilityRequestType =
  Requestify<SelfServiceEligibilityRequest>;
export type GetSelfServiceEligibilityResponseType =
  SelfServiceEligibilityResponse;
export type GetSelfServiceEligibilityErrorType = EmptyError & HubspotMetadata;
export const getSelfServiceEligibilityRpc: ChirpAvroRpcDetails<
  GetSelfServiceEligibilityRequestType,
  GetSelfServiceEligibilityResponseType,
  GetSelfServiceEligibilityErrorType,
  false
> = {
  serviceName:
    'com.hubspot.purchase.motions.rpc.PurchaseMotionsEvaluationService',
  rpcName: 'getSelfServiceEligibility',
  auth: 'app',
  fingerprint: 'gyjaiV5kkrvzxx1SiW-VnJwP6LXzGzX8',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type EvaluateAssignabilityRequestType =
  Requestify<EvaluateAssignabilityRequest>;
export type EvaluateAssignabilityResponseType = EvaluateAssignabilityResponse;
export type EvaluateAssignabilityErrorType = EmptyError & HubspotMetadata;
export const evaluateAssignabilityRpc: ChirpAvroRpcDetails<
  EvaluateAssignabilityRequestType,
  EvaluateAssignabilityResponseType,
  EvaluateAssignabilityErrorType,
  false
> = {
  serviceName:
    'com.hubspot.purchase.motions.rpc.PurchaseMotionsEvaluationService',
  rpcName: 'evaluateAssignability',
  auth: 'app',
  fingerprint: 'BzoP5ZQn4ojUw8nX1l3VLpwP6LXzGzX8',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};
